import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Checkbox,
  Tag,
  Avatar,
  Spin,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CloseCircleOutlined,
  DownOutlined,
} from "@ant-design/icons";
import "./Members.css";
import FloatLabel from "./FloatLabel"; // Import FloatLabel component
import DeleteConfirmationModal from "./DeleteConfirmationModal"; // Import the modal
import EditMemberForm from "./EditMemberForm";
import { URL } from "../helper/Mainurl";
import { apiClient } from "../utils/apiClient";
const { Option } = Select;
const roles = [
  "Co-parent",
  "School Representative",
  "Family Member",
  "Parent",
  "Other members",
];

const Members = () => {
  const token = localStorage.getItem("authToken");
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [emailForm] = Form.useForm();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState([]);
  const [members, setMembers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);
  const [roleMetaData, setRoleMetaData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFetchDone, setRoleFetchDone] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const searchTimeout = useRef(null);
  const [searchInput, setSearchInput] = useState("");
  const [isPopconfirmOpen, setIsPopconfirmOpen] = useState(false);
  const [slectedRole, setSlectedRole] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedMemberRole, setSelectedMemberRole] = useState(null);
  const [calendarSummary, setCalendarSummary] = useState(null);
  const [debouncedSearch, setDebouncedSearch] = useState(searchInput);
  const [isConfirmOpen, setIsConfimOpen] = useState(false);

  const showDeleteModal = (member) => {
    setMemberToDelete(member);
    setIsDeleteModalOpen(true);
  };
  const closeSearch = () => {
    setSearch("");
    setSearchInput("");
  };
  const handleDeleteConfirm = async () => {
    if (memberToDelete) {
      try {
        const response = await fetch(
          `${URL}/v1/member/delete/${memberToDelete.key}`,
          {
            method: "DELETE",
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const data = await response.json();
        if (response.ok && data?.code === 200) {
          setMembers(members.filter((m) => m.key !== memberToDelete.key));
          message.success("Member deleted successfully!");
        } else {
          message.error(data?.message || "Failed to delete member.");
        }
      } catch (error) {
        message.error("Failed to delete member.");
      }
    }
    setIsDeleteModalOpen(false);
    setMemberToDelete(null);
  };

  const handleDelete = (key) => {
    setMembers(members.filter((m) => m.key !== key));
    message.success("Member deleted successfully!");
  };

  const handleRoleChange = (selectedValues) => {
    // console.log("selectedValues", selectedValues);
    setRoleFilter(selectedValues);
    setCurrentPage(1);
    // loadMembers(1, selectedValues, search); // refetch
  };

  const handleCheckboxChange = (roleId) => {
    setRoleFilter((prev) =>
      prev.includes(roleId)
        ? prev.filter((id) => id !== roleId)
        : [...prev, roleId]
    );
  };
  useEffect(() => {
    const fetchMemberMetaData = async () => {
      try {
        const response = await apiClient.get("/v1/role/data");
        const data = response.data;
        if (data?.code === 200 && Array.isArray(data.data)) {
          setRoleMetaData(data.data);
        } else {
          console.error(
            "Error fetching role data:",
            data?.message || "Invalid response format"
          );
        }
      } catch (error) {
        console.error("API request failed:", error.message);
      } finally {
        setRoleFetchDone(true);
      }
    };

    if (token) {
      fetchMemberMetaData();
    } else {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler); // cleanup previous timeout
    };
  }, [searchInput]);
  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchInput(value); // update input immediately
    // setSearch(value);
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    let timer = setTimeout(() => {
      setSearch(searchInput.trim());
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [searchInput]);

  const filteredMembers = members.filter(
    (m) =>
      (!roleFilter.length ||
        roleFilter.some(
          (role) => Array.isArray(m.role) && m.role.includes(role)
        )) &&
      (!search.trim() ||
        m.name?.toLowerCase().includes(search.trim().toLowerCase()) ||
        m.email?.toLowerCase().includes(search.trim().toLowerCase()))
  );

  const showAddMemberModal = () => {
    setIsAddMemberModalOpen(true);
    emailForm.resetFields();
  };
  const showEditModal = (member) => {
    // console.log("Editing Member:", member); // Debugging log

    setEditingMember(member);
    setIsEditModalOpen(true);
  };

  const findRoleName = useCallback(
    (roleId) => {
      // console.log("roleId", roleId, roleMetaData);
      if (!roleId || !roleMetaData?.length) return "NA";
      const match = roleMetaData.find((r) => r.id == roleId);
      return match ? match.name : "NA";
    },
    [roleMetaData]
  );
  const handleAddMember = async () => {
    setSubmitLoading(true);
    try {
      const values = await emailForm.validateFields();
      if (values) {
        let invitations = [
          {
            email: values.email,
            feature: "member",
            permission_id: null,
            children: null,
          },
        ];

        const response = await fetch(`${URL}/v1/invite/send`, {
          method: "POST",
          // headers: {
          //     "Content-Type": "application/json",
          //     Authorization: `Bearer ${token}`
          // },
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "Cache-Control": "no-cache",
          },
          body: JSON.stringify({ invitations }), // Adjust role as needed
        });

        const data = await response.json();

        if (response.ok && data?.code == 200) {
          // setMembers([...members, { key: Date.now().toString() }]);
          setSubmitLoading(false);
          message.success("Invitation sent successfully!");
          setIsAddMemberModalOpen(false);
          setIsPopconfirmOpen(false);
        } else {
          setSubmitLoading(false);
          throw new Error(data?.message || "Failed to send invitation");
        }
      } else {
        setSubmitLoading(false);
        // console.log("Form validation failed:", values);
      }
    } catch (error) {
      setSubmitLoading(false);
      if (error.errorFields) {
        // Validation error, do nothing (antd will show the error)
        console.log("Validation error:", error.errorFields);
      } else {
        // Other errors (API, network, etc.)
        message.error(error.message);
      }
    }
  };
  const fetchMembers = useCallback(
    async (page, roles, searchTerm) => {
      // console.log(roles);

      try {
        let params = {};

        if (Array.isArray(roles) && roles.length > 0) {
          params.roles = roles;
        }

        if (searchTerm) {
          params.search = searchTerm;
        }
        const response = await apiClient.get(
          `/v1/member/get-members/${page}`,
          params
        );
        const data = response.data;
        setCalendarSummary(data?.data?.calendar_summary);
        if (data?.code === 200 && Array.isArray(data.data?.members)) {
          return data.data.members.map((member, index) => {
            const {
              id,
              name,
              invitee_nickname,
              email,
              phone_number,
              role_id,
              profile_image,
              has_calendar_access,
            } = member;

            return {
              key: id?.toString() || (index + 1).toString(),
              name: name,
              nickname: invitee_nickname,
              email: email,
              phone: phone_number || ``,
              role: [findRoleName(role_id)],
              role_id: role_id,
              image: profile_image,
              has_calendar_access: has_calendar_access,
            };
          });
        } else {
          console.error("Invalid API Response:", data);
          return [];
        }
      } catch (error) {
        console.error("Fetch failed:", error.message);
        return [];
      }
    },
    [token, roleMetaData]
  );

  const loadMembers = useCallback(
    async (page = 1, roles = roleFilter, query = search) => {
      if (!token || !roleFetchDone) return;
      setLoading(true);
      try {
        const list = await fetchMembers(page, roles, query);
        setMembers(list);
      } catch {
        message.error("Failed to load members.");
      } finally {
        setLoading(false);
      }
    },
    [token, roleFetchDone, fetchMembers, roleFilter, debouncedSearch]
  );

  useEffect(() => {
    loadMembers(currentPage, roleFilter, debouncedSearch);
  }, [currentPage, roleFilter, debouncedSearch, loadMembers]);

  return (
    <div className="members-page">
      <>
        <div className="members-header">
          <h2>Members</h2>
          <Button
            className="members-add-btn"
            type="primary"
            icon={<PlusOutlined />}
            onClick={showAddMemberModal}
          >
            Add Member
          </Button>
        </div>
        <div className="members-controls">
          <Select
            className="members-select"
            mode="multiple"
            onChange={handleRoleChange}
            value={roleFilter.length > 0 ? roleFilter : undefined}
            placeholder="Role"
            maxTagCount={2}
          >
            {Array.isArray(roleMetaData) && roleMetaData.length > 0 ? (
              roleMetaData
                .filter((role) => role.name.toLowerCase() !== "admin")
                .map((role) => (
                  <Option key={role.id} value={role.id} label={role.name}>
                    {role.name}
                  </Option>
                ))
            ) : (
              <Option disabled>Loading roles...</Option>
            )}
          </Select>

          <Input
            className="members-search"
            placeholder="Search"
            value={searchInput}
            onChange={handleSearchChange}
          />
        </div>
        <div className="members-result-count">
          <strong>{filteredMembers.length} Results Found</strong>
        </div>
        <div className="members-filters">
          {roleFilter.length > 0 && (
            <div className="filterSelected members-filter-chip">
              <div className="tagTitle">Role:</div>

              {roleFilter.map((roleId) => {
                const role = roleMetaData.find((r) => r.id === roleId);
                return (
                  <Tag
                    className="memberTags"
                    key={roleId}
                    closable
                    onClose={() =>
                      handleRoleChange(roleFilter.filter((r) => r !== roleId))
                    }
                  >
                    {role ? role.name : "Unknown Role"}
                  </Tag>
                );
              })}
            </div>
          )}
          {search && (
            <div className="filterSelected members-filter-chip">
              <div className="tagTitle">Searched:</div>
              <Tag className="memberTags" closable onClose={closeSearch}>
                {search}
              </Tag>
            </div>
          )}
        </div>
        <div className="members-table-wrapper">
          <Table
            className="memberTable"
            dataSource={members}
            rowKey={"key"}
            columns={[
              {
                title: "Member",
                key: "member",
                className: "member-cell-main",
                onCell: () => ({ "data-label": "Member" }),
                render: (_, record) => (
                  <div
                    className="member-cell-content"
                    style={{ display: "flex", gap: "10px" }}
                  >
                    <Avatar
                      src={record.image}
                      size={40}
                      className="member-avatar"
                    />
                    <div className="member-text">
                      <div className="member-name">{record.name || "NA"}</div>
                      <div className="member-email">
                        {record.email || "No Email"}
                      </div>
                    </div>
                  </div>
                ),
              },
              {
                title: "Nickname",
                dataIndex: "nickname",
                key: "nickname",
                // responsive: ["md"],
                onCell: () => ({ "data-label": "Nickname" }),
                render: (text) => text || "NA",
              },
              {
                title: "Phone",
                dataIndex: "phone",
                key: "phone",
                // responsive: ["lg"],
                onCell: () => ({ "data-label": "Phone" }),
                render: (text) => text || "NA",
              },
              {
                title: "Role",
                dataIndex: "role",
                className: "roleColumn",
                key: "role",
                // responsive: ["sm"],
                onCell: () => ({ "data-label": "Role" }),
                render: (roleIds) => {
                  if (!Array.isArray(roleIds)) return "NA";
                  return roleIds.join(", ");
                },
              },
              {
                title: "Actions",
                key: "actions",
                className: "member-actions-cell",
                // responsive: ["sm"],
                onCell: () => ({ "data-label": "Actions" }),
                render: (_, record) => (
                  <div className="member-actions">
                    <Button
                      className="editMemberBtn"
                      icon={<EditOutlined />}
                      onClick={() => showEditModal(record)}
                    />
                    <Button
                      className="deleteMemberBtn"
                      icon={<DeleteOutlined />}
                      onClick={() => showDeleteModal(record)}
                    />
                  </div>
                ),
              },
            ]}
            pagination={{
              current: currentPage,
              pageSize: 10,
              onChange: setCurrentPage, // Ensure it updates the state properly
            }}
            components={{
              header: {
                cell: (props) => (
                  <th
                    {...props}
                    style={{
                      background: "#FEF1FE",
                      color: "#9A4FE0",
                      fontWeight: "600",
                    }}
                  />
                ),
              },
              body: {
                cell: (props) => (
                  <td
                    {...props}
                    style={{ fontSize: "12px", color: "#1D2433" }}
                  />
                ),
              },
            }}
            loading={{
              spinning: loading,
              indicator: <Spin size="large" />,
            }}
            scroll={{ x: 720 }}
          />
        </div>
        <Modal
          title="Add Member"
          style={{ opacity: isConfirmOpen ? 0.5 : 1 }}
          open={isAddMemberModalOpen}
          onCancel={() => {
            setIsAddMemberModalOpen(false);
          }}
          onOk={!isConfirmOpen ? handleAddMember : undefined}
          width={500}
          closable={false}
          maskClosable={false}
          className="addMemberModal"
          footer={[
            <Popconfirm
              classNames="member-cancel-confirmation"
              key="cancel"
              title="Are you sure you want to cancel? Unsaved changes will be lost."
              okText="Yes"
              cancelText="No"
              zIndex={2001}
              open={isPopconfirmOpen}
              onConfirm={() => {
                setIsConfimOpen(false);
                setIsPopconfirmOpen(false);
                setTimeout(() => {
                  setIsAddMemberModalOpen(false);
                }, 0);
              }}
              cancelButtonProps={{ danger: true }}
              okButtonProps={{ danger: false }}
              onCancel={() => {
                setIsPopconfirmOpen(false);
                setIsConfimOpen(false);
                emailForm.resetFields();
              }}
            >
              <Button
                disabled={isConfirmOpen}
                onClick={() => {
                  setIsPopconfirmOpen(true);
                  setIsConfimOpen(true);
                }}
              >
                Cancel
              </Button>
            </Popconfirm>,
            <Button
              key="ok"
              type="primary"
              loading={submitLoading}
              disabled={submitLoading || isConfirmOpen}
              onClick={handleAddMember}
            >
              OK
            </Button>,
          ]}
        >
          <Form form={emailForm} layout="vertical">
            <Form.Item
              label="Email"
              name="email"
              rules={[
                {
                  required: true,
                  type: "email",
                  message: "Please enter a valid email",
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Form>
        </Modal>
        <EditMemberForm
          visible={isEditModalOpen}
          onCancel={() => setIsEditModalOpen(false)}
          member={editingMember}
          roleMetaData={roleMetaData}
          token={token}
          calendarSummary={calendarSummary}
          onUpdate={() => {
            setIsEditModalOpen(false);
            const loadMembers = async () => {
              setLoading(true);
              try {
                const fetchedMembers = await fetchMembers(1);
                setMembers(fetchedMembers);
              } catch (error) {
                message.error("Failed to load members.");
              } finally {
                setLoading(false);
              }
            };
            loadMembers();
          }}
        />

        <DeleteConfirmationModal
          visible={isDeleteModalOpen}
          onConfirm={handleDeleteConfirm}
          onCancel={() => setIsDeleteModalOpen(false)}
        />
      </>
    </div>
  );
};

export default Members;
